import type { ReactNode } from 'react';
import { Box, Paper, Stack } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';

/**
 * One message's bubble: the chat panel's own styling, taking rendered content rather than a message.
 *
 * Shared with the lookup drawer, which has its own conversation model — three flat turn kinds
 * instead of `UIMessage.parts`. Keeping this agnostic is what lets both look identical without the
 * lookup drawer having to fake a `UIMessage`, or `features/chat` learning what a lookup is.
 */
export const ChatBubble = ({
  author,
  children,
  fullWidth = false,
}: {
  author: 'user' | 'assistant';
  children: ReactNode;
  /** For a caller whose surrounding column already caps the width. */
  fullWidth?: boolean;
}) => (
  <Stack
    direction="row"
    spacing={1.5}
    alignItems="flex-start"
    justifyContent={author === 'user' ? 'flex-end' : 'flex-start'}
  >
    <Box sx={{ maxWidth: fullWidth ? '100%' : '80%', minWidth: 0 }}>
      <Paper
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: borderRadius('m'),
          ...(author === 'user'
            ? { bgcolor: 'primary.main', color: 'primary.contrastText' }
            : {
                bgcolor: 'background.paper',
                color: 'text.primary',
                border: 1,
                borderColor: 'divider',
              }),
        }}
      >
        {children}
      </Paper>
    </Box>
  </Stack>
);
